import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import {
  DOC_TYPE_ID,
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import {
  downloadFile,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import {
  ExportRatingsReportForExcelPDFBody,
  Rating,
} from "../../../hooks/report/interface";
import {
  useExportRatingsReportForExcel,
  useExportRatingsReportForPDF,
} from "../../../hooks/report/mutation";
import toast, { Toaster } from "react-hot-toast";
import StatusCodes from "../../../constants/StatusCodes";

interface RatingsReportTableProps {
  allRatings: Rating[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportRatingsReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const RatingsReportTable = ({
  allRatings,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: RatingsReportTableProps) => {
  const { t, i18n } = useTranslation();

  const { mutateAsync: ExportRatingsReportForExcel } =
    useExportRatingsReportForExcel();
  const { mutateAsync: ExportRatingsReportForPDF } =
    useExportRatingsReportForPDF();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const columns: ColumnDef<Rating>[] = useMemo(
    () => [
      {
        accessorKey: "s_AppointmentCode",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("referenceNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_FullName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Email",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("email")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "IDTypeName_En",
        header: t("idType"),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.idTypeName_En
                : row.original.idTypeName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "idNumber",
        header: t("idNumber"),
        cell: ({ row }) => {
          return (
            <span>
              {row.original.idType === DOC_TYPE_ID.NATIONAL_ID
                ? row.original.s_EIDNumber
                : row.original.idType === DOC_TYPE_ID.PASSPORT
                ? row.original.s_PassportNumber
                : row.original.idType === DOC_TYPE_ID.IQAMA
                ? row.original.s_Iqama
                : row.original.s_OthersValue}
            </span>
          );
        },
      },
      {
        accessorKey: "n_Rating",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("rating")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_Comment",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("comment")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "dt_RatingDate",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("ratingDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <p>{formattedDateTimeWithoutSec(row.original.dt_RatingDate)}</p>
          );
        },
      },
    ],
    [i18n.language, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportRatingsReportForExcel(body);
        } else {
          res = await ExportRatingsReportForPDF(body);
        }
        downloadFile(res, REPORT_NAMES.RATINGS_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportRatingsReportForExcel,
      ExportRatingsReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allRatings}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.RATINGS_REPORT}
        isExportExcel
        onExport={onExport}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default memo(RatingsReportTable);
